import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import {
  PICKUP_LOCATION,
  getEarliestPickupDateTime,
  formatDateInputValue,
  getAvailableSlotsForDate,
  getFirstAvailablePickupOption,
} from "../../checkout/checkoutUtils";
import { FaRegCalendarAlt } from "react-icons/fa";

import {
  Page,
  Inner,
  Title,
  Subtitle,
  Grid,
  Main,
  SectionCard,
  SectionHeader,
  SectionEyebrow,
  SectionTitle,
  SectionHint,
  PickupHero,
  PickupHeroTop,
  PickupBadge,
  PickupLine,
  PickupStrong,
  PickupMuted,
  PickupLink,
  DateRow,
  DateInputWrap,
  DateInput,
  DatePreview,
  SlotHint,
  SlotsGrid,
  SlotButton,
  FormGrid,
  FieldGroup,
  FieldGroupWide,
  FieldLabel,
  Input,
  TextArea,
  ErrorText,
  AlertText,
  OrderList,
  OrderItem,
  ProductImage,
  ProductInfo,
  ProductName,
  ProductMeta,
  ProductPrice,
  Summary,
  SummaryTitle,
  SummaryText,
  SummaryBox,
  SummaryRow,
  SummaryDivider,
  ButtonRow,
  PrimaryBtn,
  SecondaryBtn,
  PaymentOption,
  PaymentOptions,
  SelectField,
  CalendarIcon,
  SummaryRowMuted,
  SummaryRowTotal,
  SuccessText,
  SummaryRowDiscount,
} from "./PlasareComandaPageStyles";

const GET_USER_PROFILE = gql`
  query GetUserProfile($email: String!) {
    userProfile(email: $email) {
      id
      nume
      prenume
      email
      telefon
    }
  }
`;

const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      message
      orderId
      orderNumber
    }
  }
`;

const START_CARD_PAYMENT = gql`
  mutation StartCardPayment($input: StartCardPaymentInput!) {
    startCardPayment(input: $input) {
      ok
      orderId
      orderNumber
      paymentUrl
      paymentForm
      requiresRedirect
      message
    }
  }
`;

const VALIDATE_COUPON = gql`
  mutation ValidateCoupon($code: String!, $email: String!) {
    validateCoupon(code: $code, email: $email) {
      valid
      message
      code
      discountType
      discountValue
    }
  }
`;

function isThursday(dateStr) {
  if (!dateStr) return false;

  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return date.getDay() === 4;
}

function isSaleuri(item) {
  return item.nume?.toLowerCase().includes("saleuri");
}

function getMinGramajByPickupDate(item, pickupDate) {
  if (!isSaleuri(item)) return null;

  return isThursday(pickupDate) ? 150 : 2000;
}

function formatGramaj(g) {
  if (!g) return "";
  if (Number(g) >= 1000) return `${Number(g) / 1000} kg`;
  return `${g} g`;
}

function roundPrice(value) {
  return Math.round(Number(value || 0) * 2) / 2;
}

function formatLei(value) {
  const rounded = roundPrice(value);

  return `${rounded.toLocaleString("ro-RO", {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} lei`;
}

function formatRoDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr;
  return `${day}.${month}.${year}`;
}

function formatRoDateLong(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("ro-RO", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function calcUnitPrice(item) {
  if (item.tip_pret === "variabil" || item.tip_pret === "kg") {
    const pretPeKg = item.este_tort_personalizat
      ? Number(item.personalizare_pret_kg || item.pret || 0)
      : item.reducere_tip === "pret_nou" && Number(item.pret_redus || 0) > 0
        ? Number(item.pret_redus)
        : item.reducere_tip === "procent" &&
            Number(item.reducere_procent || 0) > 0
          ? Number(item.pret || 0) -
            (Number(item.pret || 0) * Number(item.reducere_procent || 0)) / 100
          : Number(item.pret || 0);

    return roundPrice(
      ((pretPeKg * Number(item.gramaj || 0)) / 1000) *
        Number(item.cantitate || 1),
    );
  }

  if (item.reducere_tip === "pret_nou" && Number(item.pret_redus || 0) > 0) {
    return roundPrice(Number(item.pret_redus));
  }

  if (
    item.reducere_tip === "procent" &&
    Number(item.reducere_procent || 0) > 0
  ) {
    return roundPrice(
      Number(item.pret || 0) -
        (Number(item.pret || 0) * Number(item.reducere_procent || 0)) / 100,
    );
  }

  return roundPrice(Number(item.pret || 0));
}

function calcLineTotal(item) {
  if (item.tip_pret === "variabil" || item.tip_pret === "kg") {
    const pretPeKg = item.este_tort_personalizat
      ? Number(item.personalizare_pret_kg || item.pret || 0)
      : item.reducere_tip === "pret_nou" && Number(item.pret_redus || 0) > 0
        ? Number(item.pret_redus)
        : item.reducere_tip === "procent" &&
            Number(item.reducere_procent || 0) > 0
          ? Number(item.pret || 0) -
            (Number(item.pret || 0) * Number(item.reducere_procent || 0)) / 100
          : Number(item.pret || 0);

    return roundPrice(
      ((pretPeKg * Number(item.gramaj || 0)) / 1000) *
        Number(item.cantitate || 1),
    );
  }

  return roundPrice(calcUnitPrice(item) * Number(item.cantitate || 1));
}

export default function PlasareComandaPage() {
  const navigate = useNavigate();
  const { items, totalPret } = useCart();

  const authRaw = localStorage.getItem("user");
  const authUser = authRaw ? JSON.parse(authRaw) : null;
  const authEmail = authUser?.email || "";

  const { data: profileData } = useQuery(GET_USER_PROFILE, {
    skip: !authEmail,
    variables: { email: authEmail },
    fetchPolicy: "network-only",
  });

  const [createOrder, { loading }] = useMutation(CREATE_ORDER);
  const [startCardPayment, { loading: paymentLoading }] =
    useMutation(START_CARD_PAYMENT);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");

  const [validateCoupon, { loading: couponLoading }] =
    useMutation(VALIDATE_COUPON);

  const earliestPickup = useMemo(
    () => getEarliestPickupDateTime(items),
    [items],
  );

  const firstAvailablePickup = useMemo(
    () => getFirstAvailablePickupOption(items),
    [items],
  );

  const minDate = useMemo(
    () => firstAvailablePickup?.dateStr || formatDateInputValue(earliestPickup),
    [firstAvailablePickup, earliestPickup],
  );

  const [form, setForm] = useState({
    nume: "",
    prenume: "",
    email: "",
    telefon: "",
    informatiiSuplimentare: "",
    pickupDate: minDate,
    pickupInterval: "",
    paymentMethod: "cash",
    acceptTerms: false,
    marketingConsent: false,
  });

  const [errors, setErrors] = useState({});
  const errorRefs = useRef({});

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      pickupDate: minDate,
      pickupInterval:
        prev.pickupDate === minDate
          ? prev.pickupInterval
          : firstAvailablePickup?.firstSlot?.value || "",
    }));
  }, [minDate, firstAvailablePickup]);

  useEffect(() => {
    if (!profileData?.userProfile) return;

    const u = profileData.userProfile;
    setForm((prev) => ({
      ...prev,
      nume: u.nume || "",
      prenume: u.prenume || "",
      email: u.email || "",
      telefon: u.telefon || "",
    }));
  }, [profileData]);

  const availableSlots = useMemo(() => {
    return getAvailableSlotsForDate(form.pickupDate, items);
  }, [form.pickupDate, items]);

  const subtotal = roundPrice(totalPret);

  const discountValue = appliedCoupon
    ? roundPrice((subtotal * Number(appliedCoupon.discountValue || 10)) / 100)
    : 0;

  const finalTotal = Math.max(0, roundPrice(subtotal - discountValue));

  useEffect(() => {
    if (!availableSlots.find((x) => x.value === form.pickupInterval)) {
      setForm((prev) => ({
        ...prev,
        pickupInterval: availableSlots[0]?.value || "",
      }));
    }
  }, [availableSlots, form.pickupInterval]);

  useEffect(() => {
    if (!items.length) {
      navigate("/cos");
    }
  }, [items.length, navigate]);

  const scrollToError = (errorKey) => {
    requestAnimationFrame(() => {
      errorRefs.current[errorKey]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const validate = () => {
    const next = {};
    let firstErrorKey = null;

    const addError = (key, message) => {
      if (!firstErrorKey) firstErrorKey = key;
      next[key] = message;
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    if (!form.nume.trim()) addError("nume", "Introdu numele.");
    if (!form.prenume.trim()) addError("prenume", "Introdu prenumele.");

    if (!form.email.trim()) {
      addError("email", "Introdu emailul.");
    } else if (!emailRegex.test(form.email.trim())) {
      addError("email", "Introdu o adresă de email validă.");
    }

    if (!form.telefon.trim()) {
      addError("telefon", "Introdu telefonul.");
    } else if (!phoneRegex.test(form.telefon.trim())) {
      addError("telefon", "Numărul de telefon trebuie să conțină 10 cifre.");
    }

    if (!form.pickupDate) {
      addError("pickupDate", "Alege data de ridicare.");
    }

    if (!form.pickupInterval) {
      addError("pickupInterval", "Alege ora pentru ridicare.");
    }

    if (!availableSlots.length) {
      addError(
        "pickupInterval",
        "Pentru data selectată nu există ore disponibile.",
      );
    }

    if (!form.acceptTerms) {
      addError("acceptTerms", "Trebuie să accepți termenii și condițiile.");
    }

    items.forEach((item) => {
      const minGramaj = getMinGramajByPickupDate(item, form.pickupDate);

      if (minGramaj && Number(item.gramaj || 0) < minGramaj) {
        addError(
          `gramaj_${item.cartId}`,
          `Pentru data aleasă, produsul ${item.nume} poate fi comandat doar de la minim ${formatGramaj(minGramaj)}. Te rugăm să revii în coș pentru a modifica gramajul.`,
        );
      }
    });

    setErrors(next);

    if (firstErrorKey) {
      scrollToError(firstErrorKey);
    }

    return Object.keys(next).length === 0;
  };

  useEffect(() => {
    if (!form.pickupDate || !items.length) return;

    setErrors((prev) => {
      const next = { ...prev };

      Object.keys(next).forEach((key) => {
        if (key.startsWith("gramaj_")) {
          delete next[key];
        }
      });

      items.forEach((item) => {
        const minGramaj = getMinGramajByPickupDate(item, form.pickupDate);

        if (minGramaj && Number(item.gramaj || 0) < minGramaj) {
          next[`gramaj_${item.cartId}`] =
            `Pentru data aleasă, produsul ${item.nume} poate fi comandat doar de la minim ${formatGramaj(minGramaj)}. Te rugăm să revii în coș pentru a modifica gramajul.`;
        }
      });

      return next;
    });
  }, [form.pickupDate, items]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("Introdu codul cuponului.");
      return;
    }

    if (!form.email.trim()) {
      setCouponMessage("Completează emailul înainte de aplicarea cuponului.");
      return;
    }

    try {
      const { data } = await validateCoupon({
        variables: {
          code: couponCode.trim().toUpperCase(),
          email: form.email.trim(),
        },
      });

      const result = data?.validateCoupon;
      setCouponMessage(result?.message || "");

      if (result?.valid) {
        setAppliedCoupon(result);
      } else {
        setAppliedCoupon(null);
      }
    } catch (err) {
      setCouponMessage(err.message || "Cuponul nu a putut fi verificat.");
      setAppliedCoupon(null);
    }
  };

  const handleCardPayment = async () => {
    if (!validate()) return;

    try {
      const payload = {
        user_id: profileData?.userProfile?.id || null,
        customer_nume: form.nume.trim(),
        customer_prenume: form.prenume.trim(),
        customer_email: form.email.trim(),
        customer_telefon: form.telefon.trim(),
        informatii_suplimentare: form.informatiiSuplimentare.trim(),
        pickup_date: form.pickupDate,
        pickup_interval: form.pickupInterval,
        pickup_location: PICKUP_LOCATION,
        subtotal,
        discount_value: discountValue,
        coupon_code: appliedCoupon?.code || null,
        accept_terms: form.acceptTerms,
        marketing_consent: form.marketingConsent,
        total: finalTotal,
        items: items.map((item) => ({
          product_id: item.id,
          product_name: item.nume,
          product_image: item.imagine || "",
          quantity: Number(item.cantitate || 1),
          unit_price: calcUnitPrice(item),
          line_total: calcLineTotal(item),
          gramaj: item.gramaj || null,
          mesaj_selectat: item.mesaj_selectat || "",
          mentiuni: item.mentiuni || "",
          personalizare_nume: item.personalizare_nume || "",
          personalizare_pret_kg: Number(item.personalizare_pret_kg || 0),
          descriere_decor: item.descriere_decor || "",
          poza_model_url: item.poza_model_url || "",
          termen_ore: Number(item.termen_ore || 0),
        })),
      };

      const { data } = await startCardPayment({
        variables: { input: payload },
      });

      if (data?.startCardPayment?.orderNumber) {
        localStorage.setItem(
          "pending_card_order",
          data.startCardPayment.orderNumber,
        );
      }

      if (data?.startCardPayment?.paymentUrl) {
        window.location.href = data.startCardPayment.paymentUrl;
        return;
      }

      if (data?.startCardPayment?.paymentForm) {
        document.body.insertAdjacentHTML(
          "beforeend",
          data.startCardPayment.paymentForm,
        );
        document.getElementById("netopia-form")?.submit();
        return;
      }

      throw new Error(
        data?.startCardPayment?.message || "Nu s-a putut iniția plata.",
      );
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        general: err.message || "A apărut o eroare la inițierea plății.",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload = {
        user_id: profileData?.userProfile?.id || null,
        customer_nume: form.nume.trim(),
        customer_prenume: form.prenume.trim(),
        customer_email: form.email.trim(),
        customer_telefon: form.telefon.trim(),
        informatii_suplimentare: form.informatiiSuplimentare.trim(),
        pickup_date: form.pickupDate,
        pickup_interval: form.pickupInterval,
        pickup_location: PICKUP_LOCATION,
        subtotal,
        discount_value: discountValue,
        total: finalTotal,
        coupon_code: appliedCoupon?.code || null,
        accept_terms: form.acceptTerms,
        marketing_consent: form.marketingConsent,
        items: items.map((item) => ({
          product_id: item.id,
          product_name: item.nume,
          product_image: item.imagine || "",
          quantity: Number(item.cantitate || 1),
          unit_price: calcUnitPrice(item),
          line_total: calcLineTotal(item),
          gramaj: item.gramaj || null,
          mesaj_selectat: item.mesaj_selectat || "",
          mentiuni: item.mentiuni || "",
          personalizare_nume: item.personalizare_nume || "",
          personalizare_pret_kg: Number(item.personalizare_pret_kg || 0),
          descriere_decor: item.descriere_decor || "",
          poza_model_url: item.poza_model_url || "",
          termen_ore: Number(item.termen_ore || 0),
        })),
      };

      const { data } = await createOrder({
        variables: { input: payload },
      });

      const orderNumber = data?.createOrder?.orderNumber || "";

      navigate(
        `/plata/rezultat?status=success&order=${encodeURIComponent(orderNumber)}&message=${encodeURIComponent("Comanda a fost înregistrată cu succes și va fi achitată la ridicare.")}`,
      );
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        general: err.message || "A apărut o eroare la plasarea comenzii.",
      }));
    }
  };

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    PICKUP_LOCATION,
  )}`;

  return (
    <Page>
      <Inner>
        <Title>Plasare comandă</Title>

        <Grid>
          <Main>
            <PickupHero>
              <PickupHeroTop>
                <PickupBadge>Ridicare din locație</PickupBadge>
              </PickupHeroTop>

              <PickupLine>
                <PickupStrong>Ridicarea se face de la:</PickupStrong>{" "}
                <PickupLink href={mapsUrl} target="_blank" rel="noreferrer">
                  {PICKUP_LOCATION}
                </PickupLink>
              </PickupLine>

              <PickupLine>
                <PickupStrong>Prima ridicare disponibilă:</PickupStrong>{" "}
                {firstAvailablePickup ? (
                  <>
                    <PickupMuted>
                      {firstAvailablePickup.date.toLocaleDateString("ro-RO")}
                    </PickupMuted>{" "}
                    ·{" "}
                    <PickupMuted>
                      {firstAvailablePickup.firstSlot.value}
                    </PickupMuted>
                  </>
                ) : (
                  <PickupMuted>Nu există ore disponibile momentan</PickupMuted>
                )}
              </PickupLine>
            </PickupHero>

            <SectionCard ref={(el) => (errorRefs.current.pickupDate = el)}>
              {" "}
              <SectionHeader>
                <div>
                  <SectionEyebrow>Pasul 1</SectionEyebrow>
                  <SectionTitle>Data ridicării</SectionTitle>
                  <SectionHint>
                    Alege ziua în care vrei să ridici comanda.
                  </SectionHint>
                </div>
              </SectionHeader>
              <DateRow>
                <DateInputWrap>
                  <DateInput
                    type="date"
                    min={minDate}
                    value={form.pickupDate}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        pickupDate: e.target.value,
                      }))
                    }
                  />

                  <CalendarIcon aria-hidden="true">
                    <FaRegCalendarAlt />
                  </CalendarIcon>
                </DateInputWrap>

                <DatePreview>
                  {form.pickupDate
                    ? `${formatRoDate(form.pickupDate)} · ${formatRoDateLong(form.pickupDate)}`
                    : "Nu ai selectat încă o dată"}
                </DatePreview>
              </DateRow>
              {errors.pickupDate ? (
                <ErrorText>{errors.pickupDate}</ErrorText>
              ) : null}
            </SectionCard>

            <SectionCard ref={(el) => (errorRefs.current.pickupInterval = el)}>
              {" "}
              <SectionHeader>
                <div>
                  <SectionEyebrow>Pasul 2</SectionEyebrow>
                  <SectionTitle>Alege ora ridicării</SectionTitle>
                  <SectionHint>
                    Îți recomandăm prima oră disponibilă pentru o ridicare mai
                    rapidă.
                  </SectionHint>
                </div>
              </SectionHeader>
              {availableSlots.length > 0 ? (
                <SlotHint>
                  Orele disponibile pentru{" "}
                  <strong>{formatRoDate(form.pickupDate)}</strong>
                </SlotHint>
              ) : null}
              <SelectField
                value={form.pickupInterval}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    pickupInterval: e.target.value,
                  }))
                }
                disabled={!availableSlots.length}
              >
                <option value="">Alege ora de ridicare</option>

                {availableSlots.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.value}
                  </option>
                ))}
              </SelectField>
              {!availableSlots.length ? (
                <AlertText>
                  Pentru data selectată nu există ore disponibile. Alege o altă
                  zi.
                </AlertText>
              ) : null}
              {errors.pickupInterval ? (
                <ErrorText>{errors.pickupInterval}</ErrorText>
              ) : null}
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <div>
                  <SectionEyebrow>Pasul 3</SectionEyebrow>
                  <SectionTitle>Date client</SectionTitle>
                  <SectionHint>
                    Completează datele pentru confirmarea comenzii.
                  </SectionHint>
                </div>
              </SectionHeader>

              <FormGrid>
                <FieldGroup ref={(el) => (errorRefs.current.nume = el)}>
                  {" "}
                  <FieldLabel>Nume</FieldLabel>
                  <Input
                    value={form.nume}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, nume: e.target.value }))
                    }
                    placeholder="Nume"
                  />
                  {errors.nume ? <ErrorText>{errors.nume}</ErrorText> : null}
                </FieldGroup>

                <FieldGroup ref={(el) => (errorRefs.current.prenume = el)}>
                  {" "}
                  <FieldLabel>Prenume</FieldLabel>
                  <Input
                    value={form.prenume}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, prenume: e.target.value }))
                    }
                    placeholder="Prenume"
                  />
                  {errors.prenume ? (
                    <ErrorText>{errors.prenume}</ErrorText>
                  ) : null}
                </FieldGroup>

                <FieldGroup ref={(el) => (errorRefs.current.email = el)}>
                  {" "}
                  <FieldLabel>Email</FieldLabel>
                  <Input
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="email@exemplu.ro"
                  />
                  {errors.email ? <ErrorText>{errors.email}</ErrorText> : null}
                </FieldGroup>

                <FieldGroup ref={(el) => (errorRefs.current.telefon = el)}>
                  {" "}
                  <FieldLabel>Număr de telefon</FieldLabel>
                  <Input
                    value={form.telefon}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, telefon: e.target.value }))
                    }
                    placeholder="07xxxxxxxx"
                  />
                  {errors.telefon ? (
                    <ErrorText>{errors.telefon}</ErrorText>
                  ) : null}
                </FieldGroup>

                <FieldGroupWide>
                  {" "}
                  <FieldLabel>Informații suplimentare</FieldLabel>
                  <TextArea
                    value={form.informatiiSuplimentare}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        informatiiSuplimentare: e.target.value,
                      }))
                    }
                    placeholder="Ex: detalii pentru ridicare, mențiuni despre comandă etc."
                  />
                </FieldGroupWide>

                <FieldGroupWide>
                  <FieldLabel>Metodă de plată</FieldLabel>

                  <PaymentOptions>
                    <PaymentOption
                      type="button"
                      $active={form.paymentMethod === "cash"}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, paymentMethod: "cash" }))
                      }
                    >
                      Numerar la ridicare
                    </PaymentOption>

                    <PaymentOption
                      type="button"
                      $active={form.paymentMethod === "card"}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, paymentMethod: "card" }))
                      }
                    >
                      Card online
                    </PaymentOption>
                  </PaymentOptions>
                </FieldGroupWide>

                <FieldGroupWide
                  ref={(el) => (errorRefs.current.acceptTerms = el)}
                >
                  {" "}
                  <label
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.acceptTerms}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          acceptTerms: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      Am citit și sunt de acord cu Termenii și Condițiile.
                    </span>
                  </label>
                  {errors.acceptTerms ? (
                    <ErrorText>{errors.acceptTerms}</ErrorText>
                  ) : null}
                </FieldGroupWide>

                <FieldGroupWide>
                  <label
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.marketingConsent}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          marketingConsent: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      Sunt de acord să primesc oferte și promoții prin e-mail
                      sau SMS.
                      <strong
                        style={{
                          display: "block",
                          marginTop: "8px",
                          color: "#d95e97",
                          fontSize: "13px",
                          fontWeight: "800",
                          lineHeight: "1.4",
                        }}
                      >
                        🎁 Bifează această opțiune și, dacă nu ai mai primit
                        deja unul, vei primi pe e-mail un cupon unic de 10%
                        reducere pentru următoarea comandă.
                      </strong>
                    </span>
                  </label>
                </FieldGroupWide>
              </FormGrid>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <div>
                  <SectionEyebrow>Comanda ta</SectionEyebrow>
                  <SectionTitle>Produse comandate</SectionTitle>
                  <SectionHint>
                    Verifică încă o dată selecția înainte de finalizare.
                  </SectionHint>
                </div>
              </SectionHeader>

              <OrderList>
                {items.map((item) => (
                  <OrderItem
                    key={item.cartId}
                    ref={(el) => {
                      errorRefs.current[`gramaj_${item.cartId}`] = el;
                    }}
                  >
                    {" "}
                    <ProductImage src={item.imagine} alt={item.nume} />
                    <ProductInfo>
                      <ProductName>
                        {item.nume} × {item.cantitate}
                      </ProductName>

                      {item.gramaj ? (
                        <ProductMeta>Gramaj: {item.gramaj} g</ProductMeta>
                      ) : null}

                      {errors[`gramaj_${item.cartId}`] ? (
                        <ErrorText>{errors[`gramaj_${item.cartId}`]}</ErrorText>
                      ) : null}

                      {item.mesaj_selectat ? (
                        <ProductMeta>
                          Mesaj ales: {item.mesaj_selectat}
                        </ProductMeta>
                      ) : null}

                      {item.personalizare_nume ? (
                        <ProductMeta>
                          Personalizare: {item.personalizare_nume}
                        </ProductMeta>
                      ) : null}
                    </ProductInfo>
                    <ProductPrice>
                      {formatLei(calcLineTotal(item))}
                    </ProductPrice>
                  </OrderItem>
                ))}
              </OrderList>
            </SectionCard>
          </Main>

          <Summary>
            <SummaryTitle>Sumar comandă</SummaryTitle>
            <SummaryText>
              Confirmă detaliile și finalizează comanda.
            </SummaryText>

            <SummaryBox>
              <SummaryRow>
                <span>Cupon reducere</span>
              </SummaryRow>
              <Input
                style={{
                  fontFamily: "Arial, Helvetica, sans-serif",
                  letterSpacing: "0.05em",
                  fontWeight: 400,
                }}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Ex: AHAVA-ABC123"
              />
              <SecondaryBtn
                type="button"
                onClick={handleApplyCoupon}
                disabled={couponLoading}
              >
                {couponLoading ? "Se verifică..." : "Aplică cupon"}
              </SecondaryBtn>
              {couponMessage ? (
                appliedCoupon ? (
                  <SuccessText>{couponMessage}</SuccessText>
                ) : (
                  <ErrorText>{couponMessage}</ErrorText>
                )
              ) : null}{" "}
            </SummaryBox>

            <SummaryBox>
              {appliedCoupon ? (
                <>
                  <SummaryRowMuted>
                    <span>Subtotal</span>
                    <strong>{formatLei(totalPret)}</strong>
                  </SummaryRowMuted>

                  <SummaryRowDiscount>
                    <span>Reducere cupon 10%</span>
                    <strong>-{formatLei(discountValue)}</strong>
                  </SummaryRowDiscount>

                  <SummaryDivider />
                </>
              ) : null}

              <SummaryRowTotal>
                <span>Total final</span>
                <strong>{formatLei(finalTotal)}</strong>
              </SummaryRowTotal>

              <SummaryDivider />

              <SummaryRow>
                <span>Ridicare</span>
                <b>{form.pickupDate ? formatRoDate(form.pickupDate) : "—"}</b>
              </SummaryRow>

              <SummaryRow>
                <span>Ora</span>
                <b>{form.pickupInterval || "—"}</b>
              </SummaryRow>
            </SummaryBox>

            {errors.general ? <ErrorText>{errors.general}</ErrorText> : null}

            <ButtonRow>
              <PrimaryBtn
                type="button"
                onClick={
                  form.paymentMethod === "card"
                    ? handleCardPayment
                    : handleSubmit
                }
                disabled={
                  loading ||
                  paymentLoading ||
                  !items.length ||
                  !availableSlots.length
                }
              >
                {loading || paymentLoading
                  ? "Se procesează..."
                  : form.paymentMethod === "card"
                    ? "Plătește cu cardul"
                    : "Finalizează comanda"}
              </PrimaryBtn>

              <SecondaryBtn type="button" onClick={() => navigate("/cos")}>
                Înapoi la coș
              </SecondaryBtn>
            </ButtonRow>
          </Summary>
        </Grid>
      </Inner>
    </Page>
  );
}
